import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface RecoveryStep {
  name: string;
  value: number;
  fill: string;
}

interface RecoveryData {
  steps: RecoveryStep[];
  recoveryRate?: number;
  avgResponseTime?: string;
  pendingEscalations?: number;
}

interface RecoveryFunnelProps {
  data: RecoveryData;
  loading?: boolean;
}

const RecoveryFunnel: React.FC<RecoveryFunnelProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="cc-funnel">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div className="cc-loading-shimmer" style={{ width: '100%', height: 6, borderRadius: 4 }} />
          </div>
        ))}
        <div className="cc-funnel__stats">
          <div className="cc-loading-shimmer" style={{ width: '100%', height: 60, borderRadius: 8 }} />
          <div className="cc-loading-shimmer" style={{ width: '100%', height: 60, borderRadius: 8 }} />
        </div>
      </div>
    );
  }

  if (!data || !data.steps || data.steps.length === 0) {
    return (
      <div className="cc-funnel">
        <p className="cc-muted-text">No recovery data available</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.steps.map((s) => s.value));

  return (
    <div className="cc-funnel">
      <div className="cc-funnel__bars">
        {data.steps.map((step, i) => (
          <div key={i} className="cc-funnel__step">
            <div className="cc-funnel__step-header">
              <span className="cc-funnel__step-name">{step.name}</span>
              <span className="cc-funnel__step-value" style={{ color: step.fill }}>
                {step.value}
              </span>
            </div>
            <div className="cc-funnel__bar-track">
              <div
                className="cc-funnel__bar-fill"
                style={{
                  backgroundColor: step.fill,
                  width: `${maxValue > 0 ? (step.value / maxValue) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="cc-funnel__stats">
        {data.recoveryRate !== undefined && (
          <div className="cc-funnel__stat cc-funnel__stat--green">
            <div className="cc-funnel__stat-value">{data.recoveryRate}%</div>
            <div className="cc-funnel__stat-label">Recovery Rate</div>
          </div>
        )}
        {data.avgResponseTime && (
          <div className="cc-funnel__stat cc-funnel__stat--indigo">
            <div className="cc-funnel__stat-value">{data.avgResponseTime}</div>
            <div className="cc-funnel__stat-label">Avg Response Time</div>
          </div>
        )}
      </div>

      {data.pendingEscalations !== undefined && data.pendingEscalations > 0 && (
        <div className="cc-funnel__alert">
          <AlertTriangle size={13} color="#f59e0b" />
          <span>{data.pendingEscalations} Tier 3+ escalations pending manager review</span>
        </div>
      )}
    </div>
  );
};

export default RecoveryFunnel;
